/**
 * Invoicing Page
 * Vista para gestionar facturación con integración Verifactu
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import { Icon } from "@/components/ui";
import { AlertModal } from "@/components/ui/AlertModal/AlertModal";
import { budgetsService } from "@/services/budgetsService";
import type { APIBudget, BudgetStatus } from "@/types";
import styles from "./InvoicingPage.module.css";

interface Client {
  id: string;
  name: string;
  nif: string;
  address: string;
  email: string;
}

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  total: number;
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  method: string;
  reference: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  series: string;
  client: Client;
  issueDate: string;
  dueDate: string;
  status: "draft" | "issued" | "paid" | "overdue" | "cancelled";
  items: InvoiceItem[];
  subtotal: number;
  taxAmount: number;
  total: number;
  paymentMethod: string;
  notes?: string;
  // Campos Verifactu
  verifactuId?: string;
  qrCode?: string;
  signatureValue?: string;
  timestamp?: string;
  // Pagos
  payments: Payment[];
  remainingAmount: number;
}

const STATUS_MAP: Record<BudgetStatus, Invoice["status"]> = {
  draft: "draft",
  pending: "draft",
  sended: "issued",
  approved: "paid",
  rejected: "cancelled",
  expired: "overdue",
};

function budgetToInvoice(b: APIBudget): Invoice {
  const total = parseFloat(b.total);
  const subtotal = parseFloat(b.subtotal);
  const taxAmount = total - subtotal;
  const invoiceStatus = STATUS_MAP[b.status];
  const isPaid = invoiceStatus === "paid";

  return {
    id: String(b.id),
    invoiceNumber: b.budget_number,
    series: "PRE",
    client: {
      id: String(b.client),
      name: `Cliente #${b.client}`,
      nif: "-",
      address: "-",
      email: "-",
    },
    issueDate: b.date,
    dueDate: b.valid_until,
    status: invoiceStatus,
    items: (b.items ?? []).map((item, idx) => ({
      id: String(item.id ?? idx),
      description: item.service_name,
      quantity: item.quantity,
      unitPrice: parseFloat(item.unit_price),
      taxRate: 21,
      total: parseFloat(item.line_subtotal),
    })),
    subtotal,
    taxAmount,
    total,
    paymentMethod: "transfer",
    notes: b.notes ?? undefined,
    payments: [],
    remainingAmount: isPaid ? 0 : total,
  };
}

const InvoicingPage = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadInvoices = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const response = await budgetsService.list({ page_size: 200 });
      setInvoices(response.results.map(budgetToInvoice));
    } catch {
      setLoadError("No se pudieron cargar los presupuestos");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInvoices();
  }, [loadInvoices]);

  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "error" | "success";
  }>({
    isOpen: false,
    message: "",
    type: "info",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    method: "transfer",
    reference: "",
    date: new Date().toISOString().split("T")[0],
  });

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const matchesSearch =
        invoice.invoiceNumber
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        invoice.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.client.nif.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || invoice.status === statusFilter;

      let matchesDate = true;
      if (dateFilter !== "all") {
        const invoiceDate = new Date(invoice.issueDate);
        const now = new Date();
        const diff = now.getTime() - invoiceDate.getTime();
        const days = diff / (1000 * 60 * 60 * 24);

        if (dateFilter === "week") matchesDate = days <= 7;
        else if (dateFilter === "month") matchesDate = days <= 30;
        else if (dateFilter === "quarter") matchesDate = days <= 90;
      }

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [invoices, searchTerm, statusFilter, dateFilter]);

  // Estadísticas — single pass over the array
  const stats = useMemo(() => {
    return invoices.reduce(
      (acc, inv) => {
        if (inv.status !== "draft" && inv.status !== "cancelled")
          acc.issued += inv.total;
        if (inv.status === "paid") acc.paid += inv.total;
        if (inv.status === "issued" || inv.status === "overdue")
          acc.pending += inv.remainingAmount;
        if (inv.status === "overdue") acc.overdueCount++;
        return acc;
      },
      { issued: 0, paid: 0, pending: 0, overdueCount: 0 },
    );
  }, [invoices]);

  const getStatusLabel = (status: Invoice["status"]) => {
    const labels = {
      draft: "Borrador",
      issued: "Emitida",
      paid: "Pagada",
      overdue: "Vencida",
      cancelled: "Anulada",
    };
    return labels[status];
  };

  const getStatusColor = (status: Invoice["status"]) => {
    const colors = {
      draft: "#6b7280",
      issued: "#2563eb",
      paid: "#059669",
      overdue: "#dc2626",
      cancelled: "#6b7280",
    };
    return colors[status];
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      transfer: "Transferencia",
      card: "Tarjeta",
      cash: "Efectivo",
      direct_debit: "Domiciliación",
    };
    return labels[method] || method;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleViewDetails = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailsModal(true);
  };

  const handleOpenPaymentModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      amount: invoice.remainingAmount.toString(),
      method: "transfer",
      reference: "",
      date: new Date().toISOString().split("T")[0],
    });
    setShowPaymentModal(true);
  };

  const handleRegisterPayment = () => {
    if (!selectedInvoice || !paymentForm.amount || !paymentForm.reference) {
      setAlertModal({
        isOpen: true,
        message: "Por favor, completa todos los campos",
        type: "warning",
      });
      return;
    }

    const amount = parseFloat(paymentForm.amount);
    if (amount <= 0 || amount > selectedInvoice.remainingAmount) {
      setAlertModal({
        isOpen: true,
        message:
          "El importe debe ser mayor que 0 y no exceder el importe pendiente",
        type: "error",
      });
      return;
    }

    const newPayment: Payment = {
      id: `${selectedInvoice.payments.length + 1}`,
      date: paymentForm.date,
      amount: amount,
      method: getPaymentMethodLabel(paymentForm.method),
      reference: paymentForm.reference,
    };

    const newRemainingAmount = selectedInvoice.remainingAmount - amount;
    const newStatus =
      newRemainingAmount === 0 ? "paid" : selectedInvoice.status;

    setInvoices(
      invoices.map((inv) =>
        inv.id === selectedInvoice.id
          ? {
              ...inv,
              payments: [...inv.payments, newPayment],
              remainingAmount: newRemainingAmount,
              status: newStatus,
            }
          : inv,
      ),
    );

    setShowPaymentModal(false);
    setSelectedInvoice(null);
  };

  const handleIssueInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowIssueModal(true);
  };

  const confirmIssueInvoice = async () => {
    if (!selectedInvoice) return;
    try {
      await budgetsService.patch(parseInt(selectedInvoice.id), {
        status: "sended",
      });
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === selectedInvoice.id ? { ...inv, status: "issued" } : inv,
        ),
      );
    } catch {
      setAlertModal({
        isOpen: true,
        message: "Error al emitir el presupuesto",
        type: "error",
      });
    }
    setShowIssueModal(false);
    setSelectedInvoice(null);
  };

  const handleCancelInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowCancelModal(true);
  };

  const confirmCancelInvoice = async () => {
    if (!selectedInvoice) return;
    try {
      await budgetsService.patch(parseInt(selectedInvoice.id), {
        status: "rejected",
      });
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === selectedInvoice.id
            ? { ...inv, status: "cancelled" }
            : inv,
        ),
      );
    } catch {
      setAlertModal({
        isOpen: true,
        message: "Error al anular el presupuesto",
        type: "error",
      });
    }
    setShowCancelModal(false);
    setSelectedInvoice(null);
  };

  const handleDownloadPDF = (invoice: Invoice) => {
    // Aquí iría la generación del PDF
    setAlertModal({
      isOpen: true,
      message: `Descargando PDF de ${invoice.invoiceNumber}...`,
      type: "info",
    });
  };

  const handleSendEmail = async (invoice: Invoice) => {
    try {
      await budgetsService.sendEmail(parseInt(invoice.id));
      setAlertModal({
        isOpen: true,
        message: `Presupuesto ${invoice.invoiceNumber} enviado por email`,
        type: "success",
      });
    } catch {
      setAlertModal({
        isOpen: true,
        message: "Error al enviar el presupuesto",
        type: "error",
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1>💰 Facturación</h1>
          <div className={styles.stats}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Total Facturado</span>
              <span className={styles.statValue}>
                {formatCurrency(stats.issued)}
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Cobrado</span>
              <span className={`${styles.statValue} ${styles.success}`}>
                {formatCurrency(stats.paid)}
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Pendiente</span>
              <span
                className={`${styles.statValue} ${stats.pending > 0 ? styles.warning : ""}`}
              >
                {formatCurrency(stats.pending)}
              </span>
            </div>
            {stats.overdueCount > 0 && (
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Vencidas</span>
                <span className={`${styles.statValue} ${styles.danger}`}>
                  {stats.overdueCount}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.verifactuBanner}>
        <Icon name="shield" size={20} />
        <span>
          Sistema integrado con Verifactu - Cumplimiento normativo AEAT
        </span>
      </div>

      <div className={styles.controls}>
        <div className={styles.searchBar}>
          <input
            type="text"
            placeholder="🔍 Buscar por número, cliente o NIF..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className={styles.filters}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos los estados</option>
            <option value="draft">Borradores</option>
            <option value="issued">Emitidas</option>
            <option value="paid">Pagadas</option>
            <option value="overdue">Vencidas</option>
            <option value="cancelled">Anuladas</option>
          </select>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">Todas las fechas</option>
            <option value="week">Última semana</option>
            <option value="month">Último mes</option>
            <option value="quarter">Último trimestre</option>
          </select>
        </div>
      </div>

      <div className={styles.invoicesTable}>
        <table>
          <thead>
            <tr>
              <th>Número</th>
              <th>Cliente</th>
              <th>Fecha Emisión</th>
              <th>Vencimiento</th>
              <th>Importe</th>
              <th>Pendiente</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: "center", padding: "2rem" }}
                >
                  Cargando presupuestos...
                </td>
              </tr>
            ) : loadError ? (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#dc2626",
                  }}
                >
                  {loadError}
                </td>
              </tr>
            ) : filteredInvoices.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: "center", padding: "2rem" }}
                >
                  No hay presupuestos
                </td>
              </tr>
            ) : (
              filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className={styles[invoice.status]}>
                  <td>
                    <strong>{invoice.invoiceNumber}</strong>
                    {invoice.verifactuId && (
                      <span
                        className={styles.verifactuBadge}
                        title="Registrada en Verifactu"
                      >
                        <Icon name="shield" size={14} />
                      </span>
                    )}
                  </td>
                  <td>
                    <div className={styles.clientInfo}>
                      <span className={styles.clientName}>
                        {invoice.client.name}
                      </span>
                      <span className={styles.clientNif}>
                        {invoice.client.nif}
                      </span>
                    </div>
                  </td>
                  <td>{formatDate(invoice.issueDate)}</td>
                  <td>
                    <span
                      className={
                        invoice.status === "overdue" ? styles.overdueBadge : ""
                      }
                    >
                      {formatDate(invoice.dueDate)}
                    </span>
                  </td>
                  <td className={styles.amount}>
                    {formatCurrency(invoice.total)}
                  </td>
                  <td className={styles.amount}>
                    {invoice.remainingAmount > 0 ? (
                      <span className={styles.pending}>
                        {formatCurrency(invoice.remainingAmount)}
                      </span>
                    ) : (
                      <span className={styles.paidBadge}>✓ Pagada</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={styles.statusBadge}
                      style={{
                        backgroundColor: `${getStatusColor(invoice.status)}20`,
                        color: getStatusColor(invoice.status),
                      }}
                    >
                      {getStatusLabel(invoice.status)}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionButton}
                        onClick={() => handleViewDetails(invoice)}
                        title="Ver detalles"
                      >
                        <Icon name="eye" size={18} />
                      </button>
                      {invoice.status === "draft" && (
                        <button
                          className={styles.actionButton}
                          onClick={() => handleIssueInvoice(invoice)}
                          title="Emitir factura"
                        >
                          <Icon name="check" size={18} />
                        </button>
                      )}
                      {(invoice.status === "issued" ||
                        invoice.status === "overdue") && (
                        <button
                          className={styles.actionButton}
                          onClick={() => handleOpenPaymentModal(invoice)}
                          title="Registrar pago"
                        >
                          <Icon name="dollarSign" size={18} />
                        </button>
                      )}
                      {invoice.status !== "draft" &&
                        invoice.status !== "cancelled" && (
                          <>
                            <button
                              className={styles.actionButton}
                              onClick={() => handleDownloadPDF(invoice)}
                              title="Descargar PDF"
                            >
                              <Icon name="download" size={18} />
                            </button>
                            <button
                              className={styles.actionButton}
                              onClick={() => handleSendEmail(invoice)}
                              title="Enviar por email"
                            >
                              <Icon name="mail" size={18} />
                            </button>
                          </>
                        )}
                      {invoice.status !== "cancelled" &&
                        invoice.status !== "paid" && (
                          <button
                            className={`${styles.actionButton} ${styles.danger}`}
                            onClick={() => handleCancelInvoice(invoice)}
                            title="Anular factura"
                          >
                            <Icon name="xCircle" size={18} />
                          </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Detalles Factura */}
      {showDetailsModal && selectedInvoice && (
        <div
          className={styles.modal}
          onClick={() => setShowDetailsModal(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>
                <Icon name="fileText" size={20} /> Factura{" "}
                {selectedInvoice.invoiceNumber}
              </h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowDetailsModal(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* Cliente */}
              <section className={styles.section}>
                <h3>Cliente</h3>
                <div className={styles.clientDetails}>
                  <p>
                    <strong>{selectedInvoice.client.name}</strong>
                  </p>
                  <p>NIF: {selectedInvoice.client.nif}</p>
                  <p>{selectedInvoice.client.address}</p>
                  <p>Email: {selectedInvoice.client.email}</p>
                </div>
              </section>

              {/* Fechas */}
              <section className={styles.section}>
                <h3>Fechas</h3>
                <div className={styles.dateInfo}>
                  <div>
                    <label>Fecha de emisión:</label>
                    <span>{formatDate(selectedInvoice.issueDate)}</span>
                  </div>
                  <div>
                    <label>Fecha de vencimiento:</label>
                    <span>{formatDate(selectedInvoice.dueDate)}</span>
                  </div>
                  <div>
                    <label>Forma de pago:</label>
                    <span>
                      {getPaymentMethodLabel(selectedInvoice.paymentMethod)}
                    </span>
                  </div>
                </div>
              </section>

              {/* Líneas de factura */}
              <section className={styles.section}>
                <h3>Conceptos</h3>
                <table className={styles.itemsTable}>
                  <thead>
                    <tr>
                      <th>Descripción</th>
                      <th>Cant.</th>
                      <th>P. Unit.</th>
                      <th>IVA</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.description}</td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.unitPrice)}</td>
                        <td>{item.taxRate}%</td>
                        <td>{formatCurrency(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {/* Totales */}
              <section className={styles.section}>
                <div className={styles.totals}>
                  <div className={styles.totalRow}>
                    <span>Base imponible:</span>
                    <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className={styles.totalRow}>
                    <span>IVA (21%):</span>
                    <span>{formatCurrency(selectedInvoice.taxAmount)}</span>
                  </div>
                  <div className={`${styles.totalRow} ${styles.grandTotal}`}>
                    <span>Total:</span>
                    <span>{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                </div>
              </section>

              {/* Pagos */}
              {selectedInvoice.payments.length > 0 && (
                <section className={styles.section}>
                  <h3>Historial de Pagos</h3>
                  <table className={styles.paymentsTable}>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Método</th>
                        <th>Referencia</th>
                        <th>Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.payments.map((payment) => (
                        <tr key={payment.id}>
                          <td>{formatDate(payment.date)}</td>
                          <td>{payment.method}</td>
                          <td>{payment.reference}</td>
                          <td className={styles.paymentAmount}>
                            {formatCurrency(payment.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className={styles.remainingAmount}>
                    <strong>Pendiente de pago:</strong>
                    <span>
                      {formatCurrency(selectedInvoice.remainingAmount)}
                    </span>
                  </div>
                </section>
              )}

              {/* Verifactu */}
              {selectedInvoice.verifactuId && (
                <section className={styles.section}>
                  <h3>
                    <Icon name="shield" size={20} /> Información Verifactu
                  </h3>
                  <div className={styles.verifactuInfo}>
                    <p>
                      <strong>ID Verifactu:</strong>{" "}
                      {selectedInvoice.verifactuId}
                    </p>
                    <p>
                      <strong>Timestamp:</strong> {selectedInvoice.timestamp}
                    </p>
                    {selectedInvoice.qrCode && (
                      <div className={styles.qrCode}>
                        <p>
                          <strong>Código QR de verificación:</strong>
                        </p>
                        <div className={styles.qrPlaceholder}>
                          [QR Code]
                          <br />
                          <a
                            href={selectedInvoice.qrCode}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Verificar en AEAT
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Notas */}
              {selectedInvoice.notes && (
                <section className={styles.section}>
                  <h3>Notas</h3>
                  <p className={styles.notes}>{selectedInvoice.notes}</p>
                </section>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowDetailsModal(false)}
              >
                Cerrar
              </button>
              <button
                className={styles.saveButton}
                onClick={() => handleDownloadPDF(selectedInvoice)}
              >
                📄 Descargar PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Registrar Pago */}
      {showPaymentModal && selectedInvoice && (
        <div
          className={styles.modal}
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>💶 Registrar Pago</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowPaymentModal(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.paymentSummary}>
                <p>
                  <strong>Factura:</strong> {selectedInvoice.invoiceNumber}
                </p>
                <p>
                  <strong>Cliente:</strong> {selectedInvoice.client.name}
                </p>
                <p>
                  <strong>Total factura:</strong>{" "}
                  {formatCurrency(selectedInvoice.total)}
                </p>
                <p>
                  <strong>Pendiente:</strong>{" "}
                  <span className={styles.pendingAmount}>
                    {formatCurrency(selectedInvoice.remainingAmount)}
                  </span>
                </p>
              </div>

              <div className={styles.formGroup}>
                <label>Fecha de pago *</label>
                <input
                  type="date"
                  value={paymentForm.date}
                  onChange={(e) =>
                    setPaymentForm({ ...paymentForm, date: e.target.value })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label>Importe *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={selectedInvoice.remainingAmount}
                  value={paymentForm.amount}
                  onChange={(e) =>
                    setPaymentForm({ ...paymentForm, amount: e.target.value })
                  }
                  placeholder="0.00"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Método de pago *</label>
                <select
                  value={paymentForm.method}
                  onChange={(e) =>
                    setPaymentForm({ ...paymentForm, method: e.target.value })
                  }
                >
                  <option value="transfer">Transferencia bancaria</option>
                  <option value="card">Tarjeta</option>
                  <option value="cash">Efectivo</option>
                  <option value="direct_debit">Domiciliación</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Referencia *</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) =>
                    setPaymentForm({
                      ...paymentForm,
                      reference: e.target.value,
                    })
                  }
                  placeholder="Ej: TRANS-20260121-001"
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowPaymentModal(false)}
              >
                Cancelar
              </button>
              <button
                className={styles.saveButton}
                onClick={handleRegisterPayment}
              >
                Registrar Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Emitir Factura */}
      {showIssueModal && selectedInvoice && (
        <div className={styles.modal} onClick={() => setShowIssueModal(false)}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>
                <Icon name="shield" size={20} /> Emitir Factura
              </h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowIssueModal(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <p style={{ marginBottom: "1rem", lineHeight: "1.6" }}>
                ¿Está seguro de que desea emitir la factura{" "}
                <strong>{selectedInvoice.invoiceNumber}</strong>?
              </p>
              <div className={styles.warningBox}>
                <Icon name="shield" size={20} />
                <div>
                  <strong>Importante:</strong>
                  <ul style={{ marginTop: "0.5rem", paddingLeft: "1.5rem" }}>
                    <li>Se registrará en el sistema Verifactu de la AEAT</li>
                    <li>Se generará un código QR de verificación</li>
                    <li>
                      La factura no podrá ser modificada después de emitirse
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowIssueModal(false)}
              >
                Cancelar
              </button>
              <button
                className={styles.saveButton}
                onClick={confirmIssueInvoice}
              >
                <Icon name="checkCircle" size={18} />
                Emitir Factura
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Anular Factura */}
      {showCancelModal && selectedInvoice && (
        <div className={styles.modal} onClick={() => setShowCancelModal(false)}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>
                <Icon name="xCircle" size={20} /> Anular Factura
              </h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowCancelModal(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <p style={{ marginBottom: "1rem", lineHeight: "1.6" }}>
                ¿Está seguro de que desea anular la factura{" "}
                <strong>{selectedInvoice.invoiceNumber}</strong>?
              </p>
              <div
                className={styles.warningBox}
                style={{ backgroundColor: "#fee2e2", borderColor: "#ef4444" }}
              >
                <Icon name="xCircle" size={20} />
                <div>
                  <strong>Advertencia:</strong>
                  <ul style={{ marginTop: "0.5rem", paddingLeft: "1.5rem" }}>
                    <li>Esta acción no se puede deshacer</li>
                    <li>La factura quedará marcada como anulada</li>
                    <li>Se registrará la fecha y hora de anulación</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowCancelModal(false)}
              >
                Cancelar
              </button>
              <button
                className={`${styles.saveButton} ${styles.danger}`}
                onClick={confirmCancelInvoice}
              >
                <Icon name="xCircle" size={18} />
                Anular Factura
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AlertModal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
};

export default InvoicingPage;
